import {isEqual} from 'lodash';
import Prism from 'prismjs';
import React, {memo, useEffect, useMemo, useRef} from 'react';

import type {FileBrowserFile} from './types';

const oneMb = 1024 * 1024;
const numMbToPreview = 20;
const maxPreviewSize = numMbToPreview * oneMb;
const maxLineLength = 1000;

type CodeProps = {
  file: FileBrowserFile;
  language: string;
  text: string;
};
const CodeComponent = function Code({file, language, text}: CodeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const truncatedData = useMemo(() => {
    if (text.length > maxPreviewSize) {
      // Trying to display large formatted files crashes the browser, so we don't pretty-print them,
      // and we truncate the file to 20MB. 20 may be too big - the browser definitely chugs at that point,
      // but it doesn't crash, so it's a compromise that lets users preview the file if they really want.
      // This works together with the below code that won't try to use Prism to do highlighting on large files.
      return (
        text.slice(0, maxPreviewSize) +
        `\n(file truncated to ${numMbToPreview}MB)`
      );
    }
    // Automatically reformat JSON
    let lines = text.split('\n');
    if (
      file.name.endsWith('.json') &&
      (lines.length === 1 || (lines.length === 2 && lines[1] === ''))
    ) {
      try {
        const parsed = JSON.parse(lines[0]);
        lines = JSON.stringify(parsed, undefined, 2).split('\n');
      } catch {
        // ok
      }
    }

    // Truncate long lines
    return lines
      .map(line => {
        if (line.length > maxLineLength) {
          return `${line.slice(0, maxLineLength)} (line truncated to ${maxLineLength} characters)`;
        } else {
          return line;
        }
      })
      .join('\n');
  }, [text, file.name]);

  useEffect(() => {
    if (ref.current != null) {
      Prism.highlightElement(ref.current);
    }
  });

  return (
    <div className="text-[13px] leading-[20px] p-16 border-wb-border rounded-lg border">
      {file.sizeBytes < oneMb ? (
        // When the file is under 1MB we use the normal code viewer with highlighting
        <pre>
          <code
            ref={ref}
            className={language != null ? `language-${language}` : undefined}>
            {truncatedData}
          </code>
        </pre>
      ) : (
        <pre>
          <code>{truncatedData}</code>
        </pre>
      )}
    </div>
  );
};

// isEqual should be cheap here because none of the data is particularly deep.
export const Code = memo(CodeComponent, isEqual);
